import { api } from '@/lib/api';
import type {
  CommerceOrder,
  CommerceShipment,
  CommerceShipmentIntent,
  CommerceShipmentStatus,
  PlaceCommerceOrderInput,
  PlaceCommerceOrderResponse,
  VerifyCommercePaymentInput,
  SetShipmentTrackingInput,
  FoodProduct,
  CreateFoodProductInput,
  LinkFoodCatalogProductInput,
  UpdateFoodProductInput,
  FoodDetailsRequest,
  FoodDetailsResponse,
} from './types';
import type { ProductVariant, CreateVariantInput, UpdateVariantInput } from '@/features/product-variant/types';

// ---- Owner: commerce orders ----
/**
 * Place a cross-vendor commerce order. Multipart because prescription images may ride
 * along for a PHARMACY cart with Rx lines. `payload` carries the JSON body as a string
 * field; each attached image is a `prescription` file field. Pass a FormData to axios and
 * do NOT set a manual Content-Type — axios lets the browser attach the multipart boundary
 * itself (same approach as the legacy pharmacy-order place endpoint).
 */
export async function place_commerce_order(input: PlaceCommerceOrderInput, files: File[]): Promise<PlaceCommerceOrderResponse> {
  const form = new FormData();
  form.append('payload', JSON.stringify(input));
  for (const file of files) form.append('prescription', file);
  const res = await api.post<PlaceCommerceOrderResponse>('/user/commerce/orders', form);
  return res.data;
}

/** Dummy-gateway capture (dev/test) — advances PENDING_PAYMENT → PLACED. */
export async function pay_commerce_order(id: string): Promise<CommerceOrder> {
  const res = await api.post<CommerceOrder>(`/user/commerce/orders/${id}/pay`);
  return res.data;
}

/** Razorpay client-callback verification — advances PENDING_PAYMENT → PLACED. */
export async function verify_commerce_payment(id: string, input: VerifyCommercePaymentInput): Promise<CommerceOrder> {
  const res = await api.post<CommerceOrder>(`/user/commerce/orders/${id}/pay/verify`, input);
  return res.data;
}

export async function fetch_my_commerce_orders(): Promise<CommerceOrder[]> {
  const res = await api.get<CommerceOrder[]>('/user/commerce/orders');
  return res.data;
}

export async function fetch_commerce_order(id: string): Promise<CommerceOrder> {
  const res = await api.get<CommerceOrder>(`/user/commerce/orders/${id}`);
  return res.data;
}

/** Customer cancels ONE shipment of their order — only while it is still PLACED. */
export async function cancel_commerce_shipment(order_id: string, shipment_id: string, reason?: string): Promise<CommerceOrder> {
  const res = await api.post<CommerceOrder>(
    `/user/commerce/orders/${order_id}/shipments/${shipment_id}/cancel`,
    reason ? { reason } : {},
  );
  return res.data;
}

// ---- Vendor: shipments inbox ----
export async function fetch_vendor_shipments(status?: CommerceShipmentStatus): Promise<CommerceShipment[]> {
  const res = await api.get<CommerceShipment[]>('/vendor/commerce/shipments', { params: status ? { status } : {} });
  return res.data;
}

export async function transition_commerce_shipment(
  id: string,
  intent: CommerceShipmentIntent,
  reason?: string,
): Promise<CommerceShipment> {
  const res = await api.post<CommerceShipment>(`/vendor/commerce/shipments/${id}/${intent}`, reason ? { reason } : {});
  return res.data;
}

export async function set_shipment_tracking(id: string, input: SetShipmentTrackingInput): Promise<CommerceShipment> {
  const res = await api.patch<CommerceShipment>(`/vendor/commerce/shipments/${id}/tracking`, input);
  return res.data;
}

// ---- Food catalog: public browse ----
export async function fetch_public_food_products(vendor_id: string): Promise<FoodProduct[]> {
  const res = await api.get<FoodProduct[]>(`/user/vendors/${vendor_id}/food/products`);
  return res.data;
}

// ---- Food catalog: vendor management ----
export async function fetch_my_food_products(): Promise<FoodProduct[]> {
  const res = await api.get<FoodProduct[]>('/vendor/food/products');
  return res.data;
}

export async function create_food_product(input: CreateFoodProductInput): Promise<FoodProduct> {
  const res = await api.post<FoodProduct>('/vendor/food/products', input);
  return res.data;
}

/** Same endpoint as a custom product — the link is what the body carries. */
export async function link_food_catalog_product(input: LinkFoodCatalogProductInput): Promise<FoodProduct> {
  const res = await api.post<FoodProduct>('/vendor/food/products', input);
  return res.data;
}

export async function update_food_product(id: string, input: UpdateFoodProductInput): Promise<FoodProduct> {
  const res = await api.patch<FoodProduct>(`/vendor/food/products/${id}`, input);
  return res.data;
}

export async function delete_food_product(id: string): Promise<void> {
  await api.delete(`/vendor/food/products/${id}`);
}

// ---- Food catalog: variants ----
export async function create_food_variant(product_id: string, input: CreateVariantInput): Promise<ProductVariant> {
  const res = await api.post<ProductVariant>(`/vendor/food/products/${product_id}/variants`, input);
  return res.data;
}

export async function update_food_variant(variant_id: string, input: UpdateVariantInput): Promise<ProductVariant> {
  const res = await api.patch<ProductVariant>(`/vendor/food/products/variants/${variant_id}`, input);
  return res.data;
}

export async function delete_food_variant(variant_id: string): Promise<void> {
  await api.delete(`/vendor/food/products/variants/${variant_id}`);
}

// ---- Food onboarding module details ----
const FOOD_DETAILS_URL = '/vendor/onboarding/module-details/food';

export async function save_food_details(input: FoodDetailsRequest): Promise<FoodDetailsResponse> {
  const res = await api.post<FoodDetailsResponse>(FOOD_DETAILS_URL, input);
  return res.data;
}

export async function fetch_food_details(): Promise<FoodDetailsResponse | null> {
  const res = await api.get<FoodDetailsResponse | null>(FOOD_DETAILS_URL);
  return res.data;
}
